import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/layouts/shell/Sidebar';
import { TopBar } from '@/layouts/shell/TopBar';
import { Footer } from '@/layouts/shell/Footer';
import { MobileNav } from '@/layouts/shell/MobileNav';
import { CommandPalette } from '@/layouts/shell/CommandPalette';
import { SessionTimeoutModal } from '@/components/common/SessionTimeoutModal';
import { TenantProvider } from '@/contexts/TenantContext';
import { ScrollToTop } from '@/app/ScrollToTop';
import { useUI } from '@/contexts/UIContext';
import { cn } from '@/lib/cn';

/**
 * The main authenticated shell. TenantProvider lives here (uses the router).
 * Includes a skip-to-content link (a11y), scroll/focus reset on navigation, the
 * command palette, and the session-timeout guard.
 */
export function AppLayout() {
  const { sidebarCollapsed } = useUI();

  return (
    <TenantProvider>
      {/* Skip link — first focusable element; visible only when focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-brand focus:px-4 focus:py-2 focus:text-brand-fg"
      >
        Skip to content
      </a>
      <ScrollToTop />

      <div className="min-h-screen bg-bg text-fg">
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 hidden border-r border-border transition-[width] duration-200 lg:block',
            sidebarCollapsed ? 'w-[72px]' : 'w-64',
          )}
        >
          <Sidebar />
        </aside>

        <MobileNav />
        <CommandPalette />
        <SessionTimeoutModal />

        <div className={cn('flex min-h-screen flex-col transition-[padding] duration-200', sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64')}>
          <TopBar />
          <main id="main-content" className="flex-1 px-4 py-6 outline-none sm:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-7xl">
              <Outlet />
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </TenantProvider>
  );
}
